import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RcaThunkArg } from "../../actions/actionTypes";
import { IOAuthState } from "../../store/IRcaState";
import { IOAuthAccount } from "../../models/IOAuthAccount";
import { refreshAntiForgeryToken } from "../../actions/globalConfigActions";

/**
 * The initial state for the OAuth slice.
 */
export const initialState: IOAuthState = {
  accountList: [],
  accountsLoading: false,
  accountsLoadError: "",
  loginOrLogoutError: "",
  loginOrLogoutInProgress: false,
};

/**
 * Refreshes the OAuth accounts from the server.
 */
export const refreshOAuthAccounts = createAsyncThunk<IOAuthAccount[], void, RcaThunkArg>(
  "oauth/refreshOAuthAccounts",
  async (_arg, thunkApi) => thunkApi.extra.oAuthService.getAccountsAsync()
);

/**
 * Acquires a token interactively and refreshes the anti-forgery token.
 */
export const acquireTokenInteractive = createAsyncThunk<IOAuthAccount, void, RcaThunkArg>(
  "oauth/acquireTokenInteractive",
  async (_arg, thunkApi) => {
    const credential = await thunkApi.extra.oAuthService.acquireTokensAsync();

    // Avoid storing actual tokens in the state
    const account: IOAuthAccount = {
      homeAccountId: credential.homeAccountId,
      environment: credential.environment,
      tenantId: credential.tenantId,
      username: credential.username,
    };

    await thunkApi.dispatch(refreshAntiForgeryToken());

    return account;
  }
);

/**
 * Logs out of an account and refreshes the account list.
 */
export const logoutAccount = createAsyncThunk<void, string, RcaThunkArg>("oauth/logoutAccount", async (accountId, thunkApi) => {
  try {
    await thunkApi.extra.oAuthService.logoutAccountAsync(accountId);
  } finally {
    void thunkApi.dispatch(refreshOAuthAccounts());
  }
});

const oauthSlice = createSlice({
  name: "oauth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // refreshOAuthAccounts
    builder.addCase(refreshOAuthAccounts.pending, (state) => {
      state.accountsLoading = true;
      state.accountsLoadError = "";
    });
    builder.addCase(refreshOAuthAccounts.fulfilled, (state, action) => {
      state.accountsLoading = false;
      state.accountList = action.payload;
    });
    builder.addCase(refreshOAuthAccounts.rejected, (state) => {
      state.accountsLoading = false;
      state.accountsLoadError = ClientStrings.OAuthCredentialsLoadFailedError;
    });

    // acquireTokenInteractive
    builder.addCase(acquireTokenInteractive.pending, (state) => {
      state.loginOrLogoutError = "";
      state.loginOrLogoutInProgress = true;
    });
    builder.addCase(acquireTokenInteractive.fulfilled, (state, action) => {
      state.accountList = state.accountList.filter((a) => a.homeAccountId !== action.payload.homeAccountId).concat(action.payload);
      state.loginOrLogoutInProgress = false;
    });
    builder.addCase(acquireTokenInteractive.rejected, (state) => {
      state.loginOrLogoutError = ClientStrings.OAuthSignSignInFailedError;
      state.loginOrLogoutInProgress = false;
    });

    // logoutAccount
    builder.addCase(logoutAccount.pending, (state) => {
      state.loginOrLogoutError = "";
      state.loginOrLogoutInProgress = true;
    });
    builder.addCase(logoutAccount.fulfilled, (state) => {
      state.loginOrLogoutInProgress = false;
    });
    builder.addCase(logoutAccount.rejected, (state) => {
      state.loginOrLogoutError = ClientStrings.OAuthSignOutFailedError;
      state.loginOrLogoutInProgress = false;
    });
  },
});

export default oauthSlice.reducer;
